<template>
  <b-modal
    ref="registryWizard"
    size="md"
    :title="$t('component.registryCaseWizard.title')"
    centered
    modal-class="registry-case-wizard"
    hide-footer
    @hide="onHide"
  >
    <div
      v-if="currentStep === FIRST_STEP || currentStep === SECOND_STEP"
      class="d-flex align-items-center justify-content-center flex-grow-1"
    >
      <div
        v-if="currentStep === FIRST_STEP"
        class="print-label-zone"
      >
        <div class="switch-btn p-0 d-flex align-items-center mr-4">
          <span class="mr-2">{{ $t('component.registryCaseWizard.printLabel') }}</span>
          <input
            id="toggle-print-label"
            v-model="isExternalLabel"
            type="checkbox"
            class="offscreen"
            aria-hidden="true"
          >
          <label
            for="toggle-print-label"
            class="switch mb-0"
          />
        </div>
      </div>
      <b-row class="py-4">
        <b-col class="text-center">
          <registry-wizard-icon class="wizard-icon" />
          <p class="home">
            {{ $t('component.registryCaseWizard.newExternalElementsCase') }}
          </p>
          <div
            v-if="currentStep === FIRST_STEP"
            class="wizard-home-height"
          >
            <registry-wizard-first-step
              :case-tree="caseTree"
              :first-step-has-errors="firstStepHasErrors"
              :is-external-label="isExternalLabel"
              @loadSampleTypes="loadSampleTypes()"
              @goToSecondStep="goToSecondStep"
            />
          </div>
          <div
            v-if="currentStep === SECOND_STEP"
            class="wizard-home-height"
          >
            <registry-wizard-second-step
              @addSampleAndGoToThirdStep="addSampleAndGoToThirdStep"
              @addPortionAndGoToThirdStep="addPortionAndGoToThirdStep"
              @addSubportionAndGoToThirdStep="addSubportionAndGoToThirdStep"
              @goToFirstStep="currentStep = FIRST_STEP"
            />
          </div>
        </b-col>
      </b-row>
    </div>
    <div
      v-if="currentStep === THIRD_STEP"
      class="d-flex flex-grow-1"
    >
      <div class="tree-column d-flex flex-column">
        <div class="text-center">
          <p class="column">
            {{ $t('component.registryCaseWizard.createNewElement') }}
          </p>
        </div>
        <div>
          <b-container>
            <b-row class="d-flex justify-content-center">
              <b-col
                cols="auto"
                class="px-1 d-flex flex-column align-items-center"
              >
                <div
                  v-tooltip="$tc('global.sample', 1)"
                  class="wizard-button column"
                  qa="registry-wizard-sample-small-button"
                  @click="addSampleAndGoToThirdStep"
                >
                  <i
                    class="fal fa-prescription-bottle"
                    aria-hidden="true"
                  />
                </div>
              </b-col>
              <b-col
                cols="auto"
                class="px-1 d-flex flex-column align-items-center"
              >
                <div
                  v-tooltip="$tc('component.registryCaseWizard.portion', 1)"
                  class="wizard-button column"
                  qa="registry-wizard-block-small-button"
                  @click="addPortionAndGoToThirdStep"
                >
                  <i
                    class="icon-block"
                    aria-hidden="true"
                  />
                </div>
              </b-col>
              <b-col
                v-tooltip="$tc('component.registryCaseWizard.subportion', 1)"
                cols="auto"
                class="px-1 d-flex flex-column align-items-center"
              >
                <div
                  class="wizard-button column"
                  qa="registry-wizard-slide-small-button"
                  @click="addSubportionAndGoToThirdStep"
                >
                  <i
                    class="icon-porta"
                    aria-hidden="true"
                  />
                </div>
              </b-col>
            </b-row>
          </b-container>
        </div>
        <div>
          <hr>
        </div>
        <div
          v-if="caseTree.samples && caseTree.samples.length"
          class="tree-scroll-area flex-grow-1"
        >
          <div
            v-for="(sampleItem, sampleIndex) in caseTree.samples"
            :key="sampleIndex"
            class="tree-block"
          >
            <div
              class="d-flex justify-content-between tree-item sample"
              :class="{'active':sampleItem.active, 'new':!sampleItem.visited && !sampleItem.hasErrors, 'alert':!sampleItem.active && sampleItem.hasErrors && sampleItem.validated}"
              qa="registry-wizard-sample-section"
              @click="selectElement(sampleItem, { sampleIndex })"
            >
              <div>
                <i
                  class="mx-1 fal fa-prescription-bottle mr-2 primary-dark-color"
                  aria-hidden="true"
                />
                <span>{{ sampleItem.code }}</span>
              </div>
              <div>
                <i
                  v-if="sampleItem.hasErrors && sampleItem.validated"
                  class="alert-icon fas fa-exclamation"
                  aria-hidden="true"
                />
                <i
                  v-if="!sampleItem.hasErrors && !sampleItem.visited"
                  class="new-icon fas fa-circle"
                  aria-hidden="true"
                />
              </div>
            </div>
            <div v-if="sampleItem.portions && sampleItem.portions.length">
              <div
                v-for="(portion, portionIndex) in sampleItem.portions"
                :key="portionIndex"
              >
                <div
                  class="d-flex justify-content-between tree-item portion"
                  :class="{'active':portion.active, 'new':!portion.visited && !portion.hasErrors, 'alert':!portion.active && portion.hasErrors && portion.validated}"
                  qa="registry-wizard-portion-section"
                  @click="selectElement(portion, { sampleIndex, portionIndex })"
                >
                  <div>
                    <i
                      class="icon-block primary-dark-color"
                      aria-hidden="true"
                    />
                    <span>{{ sampleItem.code }}-{{ portion.code }}</span>
                  </div>
                  <div>
                    <i
                      v-if="portion.hasErrors && portion.validated"
                      class="alert-icon fas fa-exclamation"
                      aria-hidden="true"
                    />
                    <i
                      v-if="!portion.hasErrors && !portion.visited"
                      class="new-icon fas fa-circle"
                      aria-hidden="true"
                    />
                  </div>
                </div>
                <div v-if="portion.subportions && portion.subportions.length">
                  <div
                    v-for="(subportion, subportionIndex) in portion.subportions"
                    :key="subportionIndex"
                  >
                    <div
                      class="d-flex justify-content-between tree-item subportion"
                      :class="{'active':subportion.active, 'new':!subportion.visited && !subportion.hasErrors, 'alert':!subportion.active && subportion.hasErrors && subportion.validated}"
                      qa="registry-wizard-subportion-section"
                      @click="selectElement(subportion, { sampleIndex, portionIndex, subportionIndex })"
                    >
                      <div>
                        <i
                          class="icon-porta primary-dark-color"
                          aria-hidden="true"
                        />
                        <span>{{ sampleItem.code }}-{{ portion.code }}-{{ subportion.code }}</span>
                      </div>
                      <div>
                        <i
                          v-if="subportion.hasErrors && subportion.validated"
                          class="alert-icon fas fa-exclamation"
                          aria-hidden="true"
                        />
                        <i
                          v-if="!subportion.hasErrors && !subportion.visited"
                          class="new-icon fas fa-circle"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="elementSelected"
        class="main-layout"
      >
        <div class="content px-5 py-4">
          <div class="d-flex align-items-center justify-content-between">
            <div class="title d-flex align-items-center">
              <div class="icon-zone primary-dark-color mr-2">
                <i
                  v-if="elementSelected.type === entities.SAMPLE"
                  class="fal fa-prescription-bottle"
                  aria-hidden="true"
                />
                <i
                  v-if="elementSelected.type === entities.PORTION"
                  class="icon-block"
                  aria-hidden="true"
                />
                <i
                  v-if="elementSelected.type === entities.SUBPORTION"
                  class="icon-porta"
                  aria-hidden="true"
                />
              </div>
              <span
                v-if="elementSelected.type === entities.SAMPLE"
                class="ap-title ap-title__main"
              >
                {{ elementSelected.code }}
              </span>
              <span
                v-if="elementSelected.type === entities.PORTION"
                class="ap-title ap-title__main"
              >
                {{ getCompletePortionCode }}
              </span>
              <span
                v-if="elementSelected.type === entities.SUBPORTION"
                class="ap-title ap-title__main"
              >
                {{ getCompleteSubportionCode }}
              </span>
            </div>
            <wizard-tree-arrow-navigation
              :case-tree="caseTree"
              :element-selected="elementSelected"
              :entities="entities"
              :hierarchical-indexes-of-item-selected="hierarchicalIndexesOfItemSelected"
              @selectElement="selectElement($event.element, $event.hierarchicalIndexes)"
            />
          </div>
          <div
            v-if="elementSelected.type === entities.SAMPLE"
            class="form mt-4"
          >
            <b-row>
              <b-col cols="12">
                <div
                  v-if="elementSelected.virtual"
                  class="alert-card d-flex align-items-center py-2 mb-3"
                >
                  <div>
                    <i
                      class="fas fa-exclamation-triangle"
                      aria-hidden="true"
                    />
                  </div>
                  <div style="line-height: 14px">
                    <h3 class="label-dark">
                      {{ $t('component.registryCaseWizard.disposalSample') }}
                    </h3>
                  </div>
                </div>
                <div class="mb-3">
                  <div class="form-label">
                    <label
                      :class="{ requiredlabel: (treeHasErrors && !elementSelected.sampleType && elementSelected.validated) }"
                    >
                      {{ $t('component.samplesModal.type') }}*
                      <vt-clone-toggle
                        v-model="internalSelectedFieldsForSample"
                        field="sampleType"
                        :disabled="!fixFieldsForSample"
                        qa="registry-wizard-sample-type-clone-toggle"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    v-model="elementSelected.sampleType"
                    v-validate="'required'"
                    class="nowrap"
                    qa="registry-wizard-sample-type-select"
                    track-by="id"
                    :options="sampleTypes"
                    :custom-label="defaultComboCustomLabel"
                    :allow-empty="false"
                    :class="{ requiredform: (treeHasErrors && !elementSelected.sampleType && elementSelected.validated) }"
                    @input="validateSample(elementSelected, elementSelected.validated)"
                  />
                </div>
              </b-col>
              <b-col cols="12">
                <div class="mb-3">
                  <div class="form-label">
                    <label
                      :class="{ requiredlabel: (treeHasErrors && !elementSelected.topographicCode && elementSelected.validated) }"
                    >
                      {{ $t('component.samplesModal.local') }}*
                      <vt-clone-toggle
                        v-model="internalSelectedFieldsForSample"
                        field="topographicCode"
                        :disabled="!fixFieldsForSample"
                        qa="registry-wizard-location-clone-toggle"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    v-model="elementSelected.topographicCode"
                    v-validate="'required'"
                    class="nowrap"
                    qa="registry-wizard-location-select"
                    track-by="id"
                    :options="topographicCodes"
                    :custom-label="defaultComboCustomLabel"
                    :allow-empty="false"
                    :class="{ requiredform: (treeHasErrors && !elementSelected.topographicCode && elementSelected.validated) }"
                    @input="validateSample(elementSelected, elementSelected.validated)"
                  />
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="6">
                <div class="mb-3">
                  <div class="form-label">
                    <label>
                      {{ $t('global.procedure') }}
                      <vt-clone-toggle
                        v-model="internalSelectedFieldsForSample"
                        field="procedure"
                        :disabled="!fixFieldsForSample"
                        qa="registry-wizard-procedure-clone-toggle"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    v-model="elementSelected.procedure"
                    v-validate="'required'"
                    class="nowrap"
                    qa="registry-wizard-procedure-select"
                    track-by="id"
                    :options="procedures"
                    :custom-label="defaultComboCustomLabel"
                  />
                </div>
              </b-col>
              <b-col cols="6">
                <div class="mb-3">
                  <div class="form-label">
                    <label>
                      {{ $t('component.samplesModal.diagnosis') }}
                      <vt-clone-toggle
                        v-model="internalSelectedFieldsForSample"
                        field="mostLikelyMorphologicCode"
                        :disabled="!fixFieldsForSample"
                        qa="registry-wizard-most-likely-clone-toggle"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    v-model="elementSelected.mostLikelyMorphologicCode"
                    v-validate="'required'"
                    class="nowrap"
                    qa="registry-wizard-most-likely-select"
                    track-by="id"
                    :options="mostLikelyDiagnoses"
                    :custom-label="defaultComboCustomLabel"
                  />
                </div>
              </b-col>
            </b-row>
            <div class="mt-2 d-flex justify-content-end">
              <div class="switch-btn p-0 d-flex align-items-center mr-4">
                <div class="form-label">
                  <label class="mr-2">{{ $t('grossing.content.inputFix') }}</label>
                </div>
                <input
                  id="toggle-clone-sample"
                  v-model="fixFieldsForSample"
                  type="checkbox"
                  class="offscreen"
                  aria-hidden="true"
                >
                <label
                  for="toggle-clone-sample"
                  class="switch mb-0"
                  qa="registry-wizard-clone-sample-switch"
                />
              </div>
              <vt-input
                v-model="samplesCodesToBeAdded"
                type="number"
                class="form-control-sm number-input"
                qa="registry-wizard-clone-sample-input"
              />
              <div>
                <b-button
                  class="btn__outline ml-2"
                  qa="registry-wizard-add-sample-button"
                  size="sm"
                  @click="addSamples"
                >
                  + {{
                    $tc('global.sample', 1) }}
                </b-button>
              </div>
              <b-button
                v-tooltip="$t('component.registryCaseWizard.deleteSample')"
                class="btn__icon btn__icon--primary error  pl-3 px-0 py-0"
                qa="registry-wizard-delete-button"
                @click="deleteSample"
              >
                <i
                  class="far fa-trash-alt"
                  aria-hidden="true"
                />
              </b-button>
            </div>
            <div class="mt-4 text-right">
              <p>{{ $t('component.registryCaseWizard.addElementsToSample') }}</p>
              <div class="d-flex justify-content-end">
                <div class="d-flex">
                  <vt-input
                    v-model="portionsCodesToBeAdded"
                    type="number"
                    class="form-control-sm number-input"
                    qa="registry-wizard-clone-portion-input"
                  />
                  <div>
                    <b-button
                      class="btn__outline ml-2"
                      qa="registry-wizard-add-portion-button"
                      size="sm"
                      @click="addPortionsToSample(false)"
                    >
                      + {{
                        $tc('component.registryCaseWizard.portion', 1) }}
                    </b-button>
                  </div>
                </div>
                <div class="vertical-line" />
                <div class="d-flex">
                  <vt-input
                    v-model="subportionsCodesToBeAdded"
                    type="number"
                    class="form-control-sm number-input"
                    qa="registry-wizard-clone-subportion-input"
                  />
                  <div>
                    <b-button
                      class="btn__outline ml-2"
                      qa="registry-wizard-add-subportion-button"
                      size="sm"
                      @click="addSubportionsToSample(elementSelected)"
                    >
                      + {{
                        $tc('component.registryCaseWizard.subportion', 1) }}
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="elementSelected.type === entities.PORTION"
            class="form mt-4"
          >
            <b-row>
              <b-col cols="12">
                <div
                  v-if="elementSelected.virtual"
                  class="alert-card d-flex align-items-center py-2 mb-3"
                >
                  <div>
                    <i
                      class="fas fa-exclamation-triangle"
                      aria-hidden="true"
                    />
                  </div>
                  <div style="line-height: 14px">
                    <h3 class="label-dark">
                      {{ $t('component.registryCaseWizard.disposalBlock') }}
                    </h3>
                  </div>
                </div>
                <div class="mb-3">
                  <div
                    v-if="!elementSelected.virtual"
                    class="form-label"
                  >
                    <label
                      :class="{ requiredlabel: (treeHasErrors && !elementSelected.portionType && elementSelected.validated) }"
                    >
                      {{ $t('component.registryCaseWizard.type') }}*
                      <vt-clone-toggle
                        v-if="!elementSelected.virtual"
                        v-model="internalSelectedFieldsForPortion"
                        field="portionType"
                        :disabled="!fixFieldsForPortion"
                        qa="registry-wizard-portion-type-clone-toggle"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    v-if="!elementSelected.virtual"
                    v-model="elementSelected.portionType"
                    v-validate="'required'"
                    class="nowrap"
                    qa="registry-wizard-portion-type-select"
                    track-by="potId"
                    :options="portionTypes"
                    label="potName"
                    :allow-empty="false"
                    :class="{ requiredform: (treeHasErrors && !elementSelected.portionType && elementSelected.validated) }"
                    @input="validatePortion(elementSelected, elementSelected.validated)"
                  />
                </div>
              </b-col>
            </b-row>
            <div class="mt-2 d-flex justify-content-end">
              <div
                v-if="!elementSelected.virtual"
                class="switch-btn p-0 d-flex align-items-center mr-4"
              >
                <div class="form-label">
                  <label class="mr-2">{{ $t('grossing.content.inputFix') }}</label>
                </div>
                <input
                  id="toggle-clone-portion"
                  v-model="fixFieldsForPortion"
                  type="checkbox"
                  class="offscreen"
                  aria-hidden="true"
                >
                <label
                  for="toggle-clone-portion"
                  class="switch mb-0"
                  qa="registry-wizard-clone-portion-switch"
                />
              </div>
              <vt-input
                v-model="portionsCodesToBeAdded"
                type="number"
                class="form-control-sm number-input"
                qa="registry-wizard-clone-portion-input"
              />
              <div>
                <b-button
                  class="btn__outline ml-2"
                  qa="registry-wizard-add-portion-button"
                  size="sm"
                  @click="addPortionsToSample(true)"
                >
                  + {{
                    $tc('component.registryCaseWizard.portion', 1) }}
                </b-button>
              </div>
              <b-button
                v-tooltip="$t('component.registryCaseWizard.deletePortion')"
                class="btn__icon btn__icon--primary error  pl-3 px-0 py-0"
                qa="registry-wizard-delete-portion-button"
                @click="deletePortion"
              >
                <i
                  class="far fa-trash-alt"
                  aria-hidden="true"
                />
              </b-button>
            </div>
            <div class="mt-4 text-right">
              <p>{{ $t('component.registryCaseWizard.addElementsToPortion') }}</p>
              <div class="d-flex justify-content-end">
                <div class="d-flex">
                  <vt-input
                    v-model="subportionsCodesToBeAdded"
                    type="number"
                    class="form-control-sm number-input"
                    qa="registry-wizard-clone-subportion-input"
                  />
                  <div>
                    <b-button
                      class="btn__outline ml-2"
                      qa="registry-wizard-add-slide-button"
                      size="sm"
                      @click="addSubportionsToPortion(true)"
                    >
                      +
                      {{ $tc('component.registryCaseWizard.subportion', 1) }}
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="elementSelected.type === entities.SUBPORTION"
            class="form mt-4"
          >
            <b-row>
              <b-col cols="12">
                <div class="mb-3">
                  <div class="form-label">
                    <label
                      :class="{ requiredlabel: (treeHasErrors && !elementSelected.subportionType && elementSelected.validated) }"
                    >
                      {{ $t('component.registryCaseWizard.type') }}*
                      <vt-clone-toggle
                        v-model="internalSelectedFieldsForSubportion"
                        field="subportionType"
                        :disabled="!fixFieldsForSubportion"
                        qa="registry-wizard-subportion-type-clone-toggle"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    v-model="elementSelected.subportionType"
                    v-validate="'required'"
                    class="nowrap"
                    qa="registry-wizard-subportion-type-select"
                    track-by="sptId"
                    :options="subportionTypes"
                    label="sptName"
                    :allow-empty="false"
                    :class="{ requiredform: (treeHasErrors && !elementSelected.subportionType && elementSelected.validated) }"
                    @input="validateSubportion(elementSelected, elementSelected.validated)"
                  />
                </div>
              </b-col>
              <b-col cols="12">
                <div class="mb-3">
                  <div class="form-label">
                    <label
                      :class="{ requiredlabel: (treeHasErrors && !elementSelected.priority && elementSelected.validated) }"
                    >
                      {{ $t('global.priority') }}*
                      <vt-clone-toggle
                        v-model="internalSelectedFieldsForSubportion"
                        field="priority"
                        :disabled="!fixFieldsForSubportion"
                        qa="registry-wizard-priority-clone-toggle"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    v-model="elementSelected.priority"
                    v-validate="'required'"
                    class="nowrap"
                    qa="registry-wizard-priority-select"
                    track-by="id"
                    :options="priorities"
                    label="name"
                    :allow-empty="false"
                    :class="{ requiredform: (treeHasErrors && !elementSelected.priority && elementSelected.validated) }"
                    @input="validateSubportion(elementSelected, elementSelected.validated)"
                  />
                </div>
              </b-col>
              <b-col cols="12">
                <div class="mb-3">
                  <div class="form-label">
                    <label
                      :class="{ requiredlabel: (treeHasErrors && !elementSelected.assay && elementSelected.validated) }"
                    >
                      {{ $t('component.techniquesModal.subportion') }}*
                      <vt-clone-toggle
                        v-model="internalSelectedFieldsForSubportion"
                        field="assay"
                        :disabled="!fixFieldsForSubportion"
                        qa="registry-wizard-assay-clone-toggle"
                      />
                    </label>
                  </div>
                  <vt-combo-select
                    v-model="elementSelected.assay"
                    v-validate="'required'"
                    class="nowrap"
                    qa="registry-wizard-assay-select"
                    track-by="id"
                    :options="assays"
                    label="text"
                    :custom-label="getGroupedAssayLabel"
                    :allow-empty="false"
                    :class="{ requiredform: (treeHasErrors && !elementSelected.assay && elementSelected.validated) }"
                    @input="validateSubportion(elementSelected, elementSelected.validated)"
                  />
                </div>
              </b-col>
              <b-col cols="12">
                <div class="switch-btn mb-4 p-0 d-flex align-items-center justify-content-end">
                  <div class="form-label">
                    <label class="mr-2">{{ $t('component.registryCaseWizard.assayCompleted') }}</label>
                  </div>
                  <input
                    id="toggle-completed-assay"
                    v-model="elementSelected.isAlreadyStained"
                    type="checkbox"
                    class="offscreen"
                    :disabled="disabledIsAlreadyStainedSwitchButton"
                    aria-hidden="true"
                    @change="loadAssays"
                  >
                  <label
                    for="toggle-completed-assay"
                    class="switch mb-0"
                    qa="registry-wizard-assay-completed-switch"
                  />
                </div>
              </b-col>
            </b-row>
            <div class="mt-2 d-flex justify-content-end">
              <div class="switch-btn p-0 d-flex align-items-center mr-4">
                <div class="form-label">
                  <label class="mr-2">{{ $t('grossing.content.inputFix') }}</label>
                </div>
                <input
                  id="toggle-clone-subportion"
                  v-model="fixFieldsForSubportion"
                  type="checkbox"
                  class="offscreen"
                  aria-hidden="true"
                >
                <label
                  for="toggle-clone-subportion"
                  class="switch mb-0"
                  qa="registry-wizard-clone-slide-switch"
                />
              </div>
              <vt-input
                v-model="subportionsCodesToBeAdded"
                type="number"
                class="form-control-sm number-input"
                qa="registry-wizard-clone-subportion-input"
              />
              <div>
                <b-button
                  class="btn__outline ml-2"
                  qa="registry-wizard-add-slide-button"
                  size="sm"
                  @click="addSubportionsToPortion(true)"
                >
                  + {{
                    $tc('component.registryCaseWizard.subportion', 1) }}
                </b-button>
              </div>
              <b-button
                v-tooltip="$t('component.registryCaseWizard.deleteSubportion')"
                class="btn__icon btn__icon--primary error  pl-3 px-0 py-0"
                qa="registry-wizard-delete-subportion-button"
                @click="deleteSubportion"
              >
                <i
                  class="far fa-trash-alt"
                  aria-hidden="true"
                />
              </b-button>
            </div>
          </div>
        </div>
        <div class="footer d-flex justify-content-between py-3 px-4">
          <div>
            <div class="switch-btn p-0 d-flex align-items-center mr-4">
              <span class="mr-2 printed-label-switch-title">{{ $t('registry.btn.consultation') }}</span>
              <input
                id="toggle-wizard-consultation"
                v-model="caseTree.caseOfReference"
                type="checkbox"
                class="offscreen"
                aria-hidden="true"
              >
              <label
                for="toggle-wizard-consultation"
                class="switch mb-0"
                qa="registry-wizard-consultation-switch"
              />
            </div>
          </div>
          <div class="d-flex">
            <b-button
              size="sm"
              class="btn__outline mr-3"
              qa="registry-wizard-restart-button"
              @click="restartWizard"
            >
              <i
                class="far fa-undo mr-2"
                aria-hidden="true"
              />{{ $t('component.registryCaseWizard.restartWizard') }}
            </b-button>
            <b-button
              size="sm"
              class="btn--primary"
              qa="registry-wizard-apply-button"
              @click="createCase"
            >
              <i
                class="far fa-check mr-2"
                aria-hidden="true"
              />{{ $t('component.registryCaseWizard.applyRegistry') }}
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import RegistryWizardIcon from '@/modules/RegistryModule/components/registryWizard/RegistryWizardIcon'
import commonSampleService from '@/api/resources/common/commonSampleService'
import commonPortionServices from '@/api/resources/common/commonPortionServices'
import commonSubportionServices from '@/api/resources/common/commonSubportionServices'
import { commonSampleClassificationService } from '@/api/resources/common/sample-classification/sample-classification.service'
import portionTypeService from '@/api/resources/portionTypeService'
import { assaysViewAssayService } from '@/components/assays-view/services/assays-view-assay/assays-view-assay.service'
import sessionStore from '@/session/sessionStore'
import subportionTypeServices from '@/api/resources/common/subportionTypeServices'
import registryCaseServices from '@/modules/RegistryModule/services/registryCaseServices'
import { pick } from 'lodash'
import { IDENTIFIER_SCOPES } from '@/api/resources/identifierScopesResource'
import RegistryWizardFirstStep from '@/modules/RegistryModule/components/registryWizard/RegistryWizardFirstStep'
import RegistryWizardSecondStep from '@/modules/RegistryModule/components/registryWizard/RegistryWizardSecondStep'
import WizardTreeArrowNavigation from '@/modules/RegistryModule/components/registryWizard/WizardTreeArrowNavigation'
import { SubportionTypeEnum } from '@/api/services/common/subportion-type/common-subportion-type.dto'
import { PortionTypeEnum } from '@/api/services/common/portion-type/common-portion-type.dto'
import { StoreUtils } from '@/shared/util/store-utils'

const FIRST_STEP = 'FIRST_STEP'
const SECOND_STEP = 'SECOND_STEP'
const THIRD_STEP = 'THIRD_STEP'

const PORTION_TYPE_VIRTUAL_ENUM = 'VIRTUAL'

export default {
    name: 'RegistryWizard',
    components: {
        WizardTreeArrowNavigation,
        RegistryWizardSecondStep,
        RegistryWizardFirstStep,
        RegistryWizardIcon
    },
    props: {
        sample: Object,
        isCaseValidated: Boolean
    },
    data() {
        return {
            entities: {
                SAMPLE: 'SAMPLE',
                PORTION: 'PORTION',
                SUBPORTION: 'SUBPORTION'
            },
            virtualPortionType: null,
            defaultReturnTypeId: 1,
            isExternalLabel: false,
            firstStepHasErrors: false,
            treeHasErrors: false,
            fixFieldsForSample: false,
            fixFieldsForPortion: false,
            fixFieldsForSubportion: false,
            selectedFieldsForSample: [],
            selectedFieldsForPortion: [],
            selectedFieldsForSubportion: [],
            samplesCodesToBeAdded: 1,
            portionsCodesToBeAdded: 1,
            subportionsCodesToBeAdded: 1,
            hierarchicalIndexesOfItemSelected: null,
            sampleTypes: [],
            topographicCodes: [],
            procedures: [],
            mostLikelyDiagnoses: [],
            portionTypes: [],
            assays: [],
            subportionTypes: [],
            elementSelected: null,
            initialIsAlreadyStained: true,
            FIRST_STEP: FIRST_STEP,
            SECOND_STEP: SECOND_STEP,
            THIRD_STEP: THIRD_STEP,
            currentStep: FIRST_STEP,
            caseTree: {
                caseType: null,
                priority: null,
                caseOfReference: false,
                samples: []
            },
            fixedFieldsForSubportion: {},
            fixedFieldsForPortion: {},
            fixedFieldsForSample: {}
        }
    },
    computed: {
        disabledIsAlreadyStainedSwitchButton() {
            return this.elementSelected?.assay?.isNaa
        },
        elementSelectedEntityType() {
            return this.elementSelected?.type
        },
        caseRegex() {
            return this.$store.getters.regex.filter((regex) => regex.identifierType.idtScope === IDENTIFIER_SCOPES.CASE)
        },
        labId() {
            return this.$store.getters.labId
        },
        priorities() {
            return sessionStore.state.priorities.filter(priority => priority.active)
        },
        getCompletePortionCode() {
            const sampleIndexSelected = this.hierarchicalIndexesOfItemSelected.sampleIndex
            return `${this.caseTree.samples[sampleIndexSelected].code}-${this.elementSelected.code}`
        },
        getCompleteSubportionCode() {
            const sampleIndexSelected = this.hierarchicalIndexesOfItemSelected.sampleIndex
            const portionIndexSelected = this.hierarchicalIndexesOfItemSelected.portionIndex
            return `${this.caseTree.samples[sampleIndexSelected].code}-${this.caseTree.samples[sampleIndexSelected].portions[portionIndexSelected].code}-${this.elementSelected.code}`
        },
        internalSelectedFieldsForSample: {
            get() {
                if (this.fixFieldsForSample) {
                    return this.selectedFieldsForSample
                }
                return []
            },
            set(newValue) {
                this.selectedFieldsForSample = newValue
                this.fixedFieldsForSample = pick(this.elementSelected, newValue)
            }
        },
        internalSelectedFieldsForPortion: {
            get() {
                if (this.fixFieldsForPortion) {
                    return this.selectedFieldsForPortion
                }
                return []
            },
            set(newValue) {
                this.selectedFieldsForPortion = newValue
                this.fixedFieldsForPortion = pick(this.elementSelected, newValue)
            }
        },
        internalSelectedFieldsForSubportion: {
            get() {
                if (this.fixFieldsForSubportion) {
                    return this.selectedFieldsForSubportion
                }
                return []
            },
            set(newValue) {
                this.selectedFieldsForSubportion = newValue
                this.fixedFieldsForSubportion = pick(this.elementSelected, newValue)
            }
        },
        isSubportionNumberingAtSampleLevel() {
            return StoreUtils.selectedLaboratoryConfiguration?.frontLaboratoryParameters.find(
                frontLaboratoryParameters =>
                    frontLaboratoryParameters.parameter.code === 'configuration.parameter.laboratory.idGeneration.subportion.codeUniqueScope' &&
                    frontLaboratoryParameters.value === 'SAMPLE')
        },
        hasURVActive() {
            const activeModule =
                StoreUtils.selectedLaboratoryConfiguration?.activeModules.find(
                    module => module.moduleCode === 'configuration.parameter.laboratory.module.accountancy.urv.active')
            return !!activeModule?.activeStatus
        }
    },
    watch: {
        'elementSelected.assay': {
            async handler() {
                if (this.elementSelected && this.disabledIsAlreadyStainedSwitchButton) {
                    this.elementSelected.isAlreadyStained = false
                    await this.loadAssays()
                }
            }
        }
    },
    methods: {
        checkCaseLabelRegexValidation() {
            for (let regex of this.caseRegex) {
                const caseRegexObject = new RegExp('^(?:' + regex.regex + ')$')
                if (caseRegexObject.test(this.caseTree.label)) {
                    return true
                }
            }
            this.showErrorModalGlobal(this.$t('registry.errors.invalidCaseLabel'))
            return false
        },
        mapCaseTreeSamplePortionSubportionsToRequest(caseTreeSamplePortionSubportions) {
            return caseTreeSamplePortionSubportions.map(caseTreeSamplePortionSubportion => {
                return {
                    assayId: caseTreeSamplePortionSubportion.assay.id,
                    code: caseTreeSamplePortionSubportion.code,
                    priorityId: caseTreeSamplePortionSubportion.priority.id,
                    returnTypeId: caseTreeSamplePortionSubportion.returnTypeId,
                    subportionTypeId: caseTreeSamplePortionSubportion.subportionType.sptId,
                    isAlreadyStained: caseTreeSamplePortionSubportion.isAlreadyStained
                }
            })
        },
        mapCaseTreeSamplePortionsToRequest(caseTreeSamplePortions) {
            return caseTreeSamplePortions.map(caseTreeSamplePortion => {
                return {
                    code: caseTreeSamplePortion.code,
                    portionTypeId: caseTreeSamplePortion.portionType.potId,
                    returnTypeId: caseTreeSamplePortion.returnTypeId,
                    subportions: this.mapCaseTreeSamplePortionSubportionsToRequest(caseTreeSamplePortion.subportions)
                }
            })
        },
        mapCaseTreeSamplesToRequest(caseTreeSamples) {
            return caseTreeSamples.map(caseTreeSample => {
                return {
                    code: caseTreeSample.code,
                    mostLikelyMorphologicCodeId: caseTreeSample.mostLikelyMorphologicCode && caseTreeSample.mostLikelyMorphologicCode.id,
                    portions: this.mapCaseTreeSamplePortionsToRequest(caseTreeSample.portions),
                    procedureId: caseTreeSample.procedure && caseTreeSample.procedure.id,
                    sampleTypeId: caseTreeSample.sampleType.id,
                    topographicCodeId: caseTreeSample.topographicCode.id,
                    sampleOnSlide: caseTreeSample.sampleOnSlide
                }
            })
        },
        mapCaseTreeToRequest(caseTree) {
            return {
                caseOfReference: caseTree.caseOfReference,
                isExternalLabel: this.isExternalLabel,
                caseTypeId: caseTree.caseType.id,
                label: caseTree.label,
                patientId: caseTree.patientId,
                priorityId: caseTree.priority.id,
                samples: this.mapCaseTreeSamplesToRequest(caseTree.samples)
            }
        },
        async createCase() {
            if (!this.validateTree()) {
                this.showErrorModalGlobal(this.$t('global.incompleteForm'))
                return
            }
            const request = this.mapCaseTreeToRequest(this.caseTree)
            this.updateLoading(true)
            const [err, response] = await this.toCall(registryCaseServices.createCaseByWizard(this.labId, request))
            this.updateLoading(false)
            if (err) {
                this.$_showErrorModalFromErrorResponse(err, this.$t('registry.index.saveFormError'))
                return
            }
            this.$router.push({ name: 'registryEditCase', params: { id: response.casId } })
            this.$emit('createCase')
        },
        validateTree() {
            this.treeHasErrors = false
            this.caseTree.samples.forEach((sample) => {
                this.validateSample(sample, true)
                sample.portions.forEach((portion) => {
                    this.validatePortion(portion, true)
                    portion.subportions.forEach((subportion) => {
                        this.validateSubportion(subportion, true)
                    })
                })
            })
            this.$forceUpdate()
            return !this.treeHasErrors
        },
        validateSample(sample, needToValidate) {
            if (needToValidate) {
                sample.hasErrors = !sample.sampleType || !sample.topographicCode
                sample.validated = true
                this.treeHasErrors = this.treeHasErrors || sample.hasErrors
            }
            this.$forceUpdate()
        },
        validatePortion(portion, needToValidate) {
            if (needToValidate) {
                portion.hasErrors = !portion.portionType
                portion.validated = true
                this.treeHasErrors = this.treeHasErrors || portion.hasErrors
            }
            this.$forceUpdate()
        },
        validateSubportion(subportion, needToValidate) {
            if (needToValidate) {
                subportion.hasErrors = !subportion.assay || !subportion.priority || !subportion.subportionType
                subportion.validated = true
                this.treeHasErrors = this.treeHasErrors || subportion.hasErrors
            }
            this.$forceUpdate()
        },
        async deleteSample() {
            if (this.caseTree.samples.length === 1) {
                const deleteConfirmationOneElement = await this.showConfirmationModalGlobal(this.$t('registry.cases.wizardStartAgain'))
                if (deleteConfirmationOneElement) {
                    this.caseTree.samples.splice(0, 1)
                    this.currentStep = SECOND_STEP
                }
            } else {
                const deleteConfirmation = await this.showConfirmationModalGlobal()
                if (deleteConfirmation) {
                    const sampleIndex = this.hierarchicalIndexesOfItemSelected.sampleIndex
                    this.caseTree.samples.splice(sampleIndex, 1)
                    await this.selectElement(this.caseTree.samples[0], { sampleIndex })
                }
            }
        },
        async deletePortion() {
            const deleteConfirmation = await this.showConfirmationModalGlobal()
            if (deleteConfirmation) {
                const sampleIndex = this.hierarchicalIndexesOfItemSelected.sampleIndex
                const portionIndex = this.hierarchicalIndexesOfItemSelected.portionIndex
                this.caseTree.samples[sampleIndex].portions.splice(portionIndex, 1)
                await this.selectElement(this.caseTree.samples[sampleIndex], { sampleIndex })
            }
        },
        async deleteSubportion() {
            const deleteConfirmation = await this.showConfirmationModalGlobal()
            if (deleteConfirmation) {
                const sampleIndex = this.hierarchicalIndexesOfItemSelected.sampleIndex
                const portionIndex = this.hierarchicalIndexesOfItemSelected.portionIndex
                const subportionIndex = this.hierarchicalIndexesOfItemSelected.subportionIndex
                this.caseTree.samples[sampleIndex].portions[portionIndex].subportions.splice(subportionIndex, 1)
                await this.selectElement(this.caseTree.samples[sampleIndex].portions[portionIndex], {
                    sampleIndex,
                    portionIndex
                })
            }
        },
        getGroupedAssayLabel(assay) {
            if (this.hasURVActive) {
                return `${assay.name} - ${assay.assaygroup.name} ( URV ${assay.urv} )`
            } else {
                return `${assay.name} - ${assay.assaygroup.name}`
            }
        },
        async selectElement(element, hierarchicalIndexesOfItemSelected) {
            if (this.elementSelected) this.elementSelected.active = false
            this.hierarchicalIndexesOfItemSelected = hierarchicalIndexesOfItemSelected
            this.elementSelected = element
            this.elementSelected.active = true
            this.elementSelected.visited = true
            this.samplesCodesToBeAdded = 1
            this.portionsCodesToBeAdded = 1
            this.subportionsCodesToBeAdded = 1
            if (this.elementSelected.type === this.entities.SUBPORTION) {
                this.elementSelected.subportionType = this.elementSelected.subportionType ?? this.subportionTypes.find(subportionType => subportionType.subportionTypeEnum === SubportionTypeEnum.SLIDE)
                this.elementSelected.priority = this.elementSelected.priority ?? this.caseTree.priority
                await this.loadAssays()
            } else if (this.elementSelected.type === this.entities.PORTION) {
                if (!this.elementSelected.virtual) {
                    const hasPortionFixedFields = this.fixFieldsForPortion && this.selectedFieldsForPortion.length
                    if (!hasPortionFixedFields) {
                        this.elementSelected.portionType = this.elementSelected.portionType ?? this.portionTypes.find(portionType => portionType.portionTypeEnum === PortionTypeEnum.PARAFFIN_BLOCK)
                    }
                }
            }
        },
        async addSamples() {
            const sampleNumbersUsed = this.caseTree.samples.map((sample) => sample.code)
            const sampleCodes = await this.getNextSampleLabels(this.samplesCodesToBeAdded, sampleNumbersUsed)
            let fixContent = {}
            if (this.fixFieldsForSample && this.selectedFieldsForSample.length) {
                fixContent = pick(this.elementSelected, this.selectedFieldsForSample)
            }
            this.caseTree.samples.push(
                ...sampleCodes.map((code) => {
                    return {
                        returnTypeId: this.defaultReturnTypeId,
                        type: this.entities.SAMPLE,
                        sampleOnSlide: false,
                        portions: [],
                        code,
                        ...fixContent
                    }
                })
            )
            this.sortByKey(this.caseTree.samples, 'code')
        },
        async addPortionsToSample(canUseFixFields) {
            const sampleIndexSelected = this.hierarchicalIndexesOfItemSelected.sampleIndex
            const sampleSelected = this.caseTree.samples[sampleIndexSelected]
            const portionNumbersUsed = sampleSelected.portions.map((portion) => portion.code)
            const portionCodes = await this.getNextPortionLabels(this.portionsCodesToBeAdded, portionNumbersUsed)
            let fixContent = {}
            if (this.fixFieldsForPortion && this.selectedFieldsForPortion.length && canUseFixFields) {
                fixContent = pick(this.elementSelected, this.selectedFieldsForPortion)
            }
            sampleSelected.portions.push(
                ...portionCodes.map((code) => {
                    return {
                        returnTypeId: this.defaultReturnTypeId,
                        type: this.entities.PORTION,
                        subportions: [],
                        portionType: this.portionTypes.find(portionType => portionType.portionTypeEnum === PortionTypeEnum.PARAFFIN_BLOCK),
                        code,
                        ...fixContent
                    }
                })
            )
            this.sortByKey(sampleSelected.portions, 'code')
        },
        async addSubportionsToPortion(canUseFixFields) {
            const sampleIndexSelected = this.hierarchicalIndexesOfItemSelected.sampleIndex
            const portionIndexSelected = this.hierarchicalIndexesOfItemSelected.portionIndex
            const portionSelected = this.caseTree.samples[sampleIndexSelected].portions[portionIndexSelected]
            let subportionCodesUsed = null
            if (this.isSubportionNumberingAtSampleLevel) {
                subportionCodesUsed = this.caseTree.samples[sampleIndexSelected].portions.map((portion) => portion.subportions.map((subportion) => subportion.code))
            } else {
                subportionCodesUsed = portionSelected.subportions.map((subportion) => subportion.code)
            }
            const subportionCodes = await this.getNextSubportionLabels(this.subportionsCodesToBeAdded, subportionCodesUsed)
            let fixContent = {}
            if (this.fixFieldsForSubportion && this.selectedFieldsForSubportion.length && canUseFixFields) {
                fixContent = pick(this.elementSelected, this.selectedFieldsForSubportion)
            }
            const isAlreadyStained = this.elementSelected?.isAlreadyStained ?? this.initialIsAlreadyStained
            portionSelected.subportions.push(
                ...subportionCodes.map((code) => {
                    return {
                        returnTypeId: this.defaultReturnTypeId,
                        type: this.entities.SUBPORTION,
                        isAlreadyStained,
                        code,
                        ...fixContent
                    }
                })
            )
            this.sortByKey(portionSelected.subportions, 'code')
        },
        async addSubportionsToSample(sample) {
            const virtualPortion = sample.portions && sample.portions.find((portion) => portion.virtual)
            if (virtualPortion) {
                let subportionCodesUsed = null
                if (this.isSubportionNumberingAtSampleLevel) {
                    subportionCodesUsed = sample.portions.map((portion) => portion.subportions.map((subportion) => subportion.code))
                } else {
                    subportionCodesUsed = virtualPortion.subportions.map((subportion) => subportion.code)
                }

                const subportionCodesForVirtualPortion = await this.getNextSubportionLabels(this.subportionsCodesToBeAdded, subportionCodesUsed)
                const isAlreadyStained = this.elementSelected?.isAlreadyStained ?? this.initialIsAlreadyStained
                virtualPortion.subportions.push(
                    ...subportionCodesForVirtualPortion.map((code) => {
                        return {
                            returnTypeId: this.defaultReturnTypeId,
                            type: this.entities.SUBPORTION,
                            isAlreadyStained,
                            code
                        }
                    }))
                this.sortByKey(virtualPortion.subportions, 'code')
            } else {
                const portionCode = await this.getNextPortionLabels(1, [], true)
                let subportionCodesUsed = null
                let subportionCode = null
                if (this.isSubportionNumberingAtSampleLevel) {
                    subportionCodesUsed = sample.portions.map((portion) => portion.subportions.map((subportion) => subportion.code))
                }
                const isAlreadyStained = this.elementSelected?.isAlreadyStained ?? this.initialIsAlreadyStained
                subportionCode = await this.getNextSubportionLabels(this.subportionsCodesToBeAdded, subportionCodesUsed)
                sample.portions.push({
                    type: this.entities.PORTION,
                    virtual: true,
                    portionType: this.virtualPortionType,
                    returnTypeId: this.defaultReturnTypeId,
                    code: portionCode[0],
                    subportions: subportionCode.map((code) => {
                        return {
                            type: this.entities.SUBPORTION,
                            isAlreadyStained,
                            code
                        }
                    })
                })
                this.sortByKey(sample.portions, 'code')
            }
        },
        async addSampleAndGoToThirdStep() {
            const sampleNumbersUsed = this.caseTree.samples.map((sample) => sample.code)
            const sampleCode = await this.getNextSampleLabels(1, sampleNumbersUsed)

            const fixedFieldsForSample = this.fixFieldsForSample ? this.fixedFieldsForSample : {}

            this.caseTree.samples.push({
                type: this.entities.SAMPLE,
                returnTypeId: this.defaultReturnTypeId,
                code: sampleCode[0],
                sampleOnSlide: false,
                ...fixedFieldsForSample,
                portions: []
            })
            this.sortByKey(this.caseTree.samples, 'code')
            const sampleIndex = this.caseTree.samples.length - 1
            await this.selectElement(this.caseTree.samples[sampleIndex], { sampleIndex })
            this.currentStep = THIRD_STEP
        },
        async addPortionAndGoToThirdStep() {
            const sampleNumbersUsed = this.caseTree.samples.map((sample) => sample.code)
            const sampleCode = await this.getNextSampleLabels(1, sampleNumbersUsed)
            const portionCode = await this.getNextPortionLabels(1)

            const fixedFieldsForPortion = this.fixFieldsForPortion ? this.fixedFieldsForPortion : {}
            const fixedFieldsForSample = this.fixFieldsForSample ? this.fixedFieldsForSample : {}

            this.caseTree.samples.push({
                code: sampleCode[0],
                type: this.entities.SAMPLE,
                virtual: true,
                returnTypeId: this.defaultReturnTypeId,
                sampleOnSlide: true,
                ...fixedFieldsForSample,
                portions: [
                    {
                        type: this.entities.PORTION,
                        code: portionCode[0],
                        returnTypeId: this.defaultReturnTypeId,
                        ...fixedFieldsForPortion,
                        subportions: []
                    }
                ]
            })
            this.sortByKey(this.caseTree.samples, 'code')
            const sampleIndex = this.caseTree.samples.length - 1
            const portionIndex = 0
            await this.selectElement(this.caseTree.samples[sampleIndex].portions[portionIndex], {
                sampleIndex,
                portionIndex
            })
            this.currentStep = THIRD_STEP
        },
        async addSubportionAndGoToThirdStep() {
            const sampleNumbersUsed = this.caseTree.samples.map((sample) => sample.code)
            const sampleCode = await this.getNextSampleLabels(1, sampleNumbersUsed)
            const portionCode = await this.getNextPortionLabels(1, [], true)
            const subportionCode = await this.getNextSubportionLabels(1)

            let fixedFieldsForSubportion = this.fixFieldsForSubportion ? this.fixedFieldsForSubportion : {}
            let fixedFieldsForSample = this.fixFieldsForSample ? this.fixedFieldsForSample : {}
            const isAlreadyStained = this.elementSelected?.isAlreadyStained ?? this.initialIsAlreadyStained

            this.caseTree.samples.push({
                code: sampleCode[0],
                type: this.entities.SAMPLE,
                virtual: true,
                returnTypeId: this.defaultReturnTypeId,
                sampleOnSlide: true,
                ...fixedFieldsForSample,
                portions: [
                    {
                        code: portionCode[0],
                        type: this.entities.PORTION,
                        returnTypeId: this.defaultReturnTypeId,
                        virtual: true,
                        portionType: this.virtualPortionType,
                        subportions: [
                            {
                                returnTypeId: this.defaultReturnTypeId,
                                code: subportionCode[0],
                                type: this.entities.SUBPORTION,
                                isAlreadyStained,
                                ...fixedFieldsForSubportion
                            }
                        ]
                    }
                ]
            })
            this.sortByKey(this.caseTree.samples, 'code')
            const sampleIndex = this.caseTree.samples.length - 1
            const portionIndex = 0
            const subportionIndex = 0
            const subportionToBeSelected = this.caseTree.samples[sampleIndex].portions[portionIndex].subportions[subportionIndex]
            await this.selectElement(subportionToBeSelected, {
                sampleIndex,
                portionIndex,
                subportionIndex
            })
            this.currentStep = THIRD_STEP
        },
        async getNextSampleLabels(quantity, sampleNumbers) {
            const params = {
                quantity,
                sampleNumbers
            }
            const [err, response] = await this.toCall(commonSampleService.getNextSampleNumbers(this.labId, params))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            return response
        },
        async getNextPortionLabels(numberOfCodesToBeGenerated, portionCodes, hasVirtualPortion = false) {
            const params = {
                numberOfCodesToBeGenerated,
                hasVirtualPortion,
                portionCodes
            }
            const [err, response] = await this.toCall(commonPortionServices.getNextPortionCodes(this.labId, params))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            return response
        },
        async getNextSubportionLabels(numberOfCodesToBeGenerated, subportionCodes) {
            const params = {
                numberOfCodesToBeGenerated,
                subportionCodes
            }
            const [err, response] = await this.toCall(commonSubportionServices.getNextSubportionCodes(this.labId, params))
            if (err) {
                this.showErrorModalGlobal()
                throw err
            }
            return response
        },
        goToSecondStep() {
            if (!this.caseTree.caseType || !this.caseTree.priority || (this.isExternalLabel && !this.caseTree.label)) {
                this.showErrorModalGlobal(this.$t('global.incompleteForm'))
                this.firstStepHasErrors = true
                return
            }
            if (this.isExternalLabel && !this.checkCaseLabelRegexValidation()) {
                return
            }
            this.firstStepHasErrors = false
            this.currentStep = SECOND_STEP
        },
        async show(patId) {
            this.updateLoading(true)
            await Promise.all([
                this.loadMostLikelyMorphologicCodes(),
                this.loadProcedures(),
                this.loadTopographicCodes(),
                this.loadPortionTypes(),
                this.loadSubportionTypes()
            ])
            this.updateLoading(false)
            this.caseTree.patientId = patId
            this.$refs.registryWizard.show()
        },
        hide() {
            this.$refs.registryWizard?.hide()
        },
        onHide() {
            this.$_resetValidator(this.$validator)
            this.$_cleanComponentData()
            this.$emit('hide')
        },
        restartWizard() {
            this.currentStep = FIRST_STEP
            this.caseTree = {
                caseType: null,
                priority: null,
                patientId: this.caseTree.patientId,
                caseOfReference: false,
                samples: []
            }
        },
        async loadTopographicCodes() {
            const [err, response] = await this.toCall(commonSampleClassificationService.getCommonTopographicCodesSampleClassificationsByLaboratory(
                { laboratoryId: this.labId }
            ))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.topographicCodes = response
        },
        async loadSampleTypes() {
            const [err, response] = await this.toCall(commonSampleClassificationService.getCommonSampleTypeSampleClassificationsByLaboratory(
                {
                    laboratoryId: this.labId,
                    caseTypeIds: [this.caseTree.caseType.id]
                }
            ))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.sampleTypes = response
        },
        async loadProcedures() {
            const [err, response] = await this.toCall(commonSampleClassificationService.getCommonProceduresSampleClassificationsByLaboratory(
                { laboratoryId: this.labId }
            ))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.procedures = response
        },
        async loadMostLikelyMorphologicCodes() {
            const [err, response] = await this.toCall(commonSampleClassificationService.getCommonMostLikelyMorphologicCodesSampleClassificationsByLaboratory(
                { laboratoryId: this.labId }
            ))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.mostLikelyDiagnoses = response
        },
        async loadPortionTypes() {
            const [err, response] = await this.toCall(portionTypeService.findAll())
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.virtualPortionType = response.find((portionType) => portionType.portionTypeEnum === PORTION_TYPE_VIRTUAL_ENUM)
            this.portionTypes = response.filter((portionType) => portionType.portionTypeEnum !== PORTION_TYPE_VIRTUAL_ENUM)
        },
        async loadAssays() {
            let assays = []
            if (this.elementSelected?.isAlreadyStained) {
                const responseCompletedAssays = await this.$_toCallUnique(assaysViewAssayService.getCompletedAssays(this.labId))
                if (responseCompletedAssays) {
                    assays = responseCompletedAssays
                }
            } else {
                const responseAssays = await this.$_toCallUnique(assaysViewAssayService.getAssays(this.labId))
                if (responseAssays) {
                    assays = responseAssays
                }
            }
            this.assays = assays
        },
        async loadSubportionTypes() {
            const [err, response] = await this.toCall(subportionTypeServices.getSubportionTypes())
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.subportionTypes = response.filter((subportionType) => subportionType.sptActive)
        },
        sortByKey(elements, key) {
            // TODO: Pasarlo a global
            elements.sort((a, b) => a[key].localeCompare(b[key], undefined, { numeric: true }))
        }
    }
}
</script>

<style lang="scss">
    .registry-case-wizard .alert-card {
        border: 1px solid vt-color(grid-general-border);
        border-radius: 3px;
        border-left: 3px solid vt-color(warning);
        background: vt-color(warning, .8);
    }

    .registry-case-wizard .alert-card i {
        color: vt-color(warning);
        font-size: font-size(very-big);
        margin: 0 10px;
    }

    .registry-case-wizard .alert-card h3 {
        margin-bottom: 0;
        font-size: font-size(normal);
        font-weight: bold;
    }

    .registry-case-wizard .alert-card span {
        font-size: font-size(small);
    }

    .registry-case-wizard .print-label-zone {
        position: absolute;
        bottom: 16px;
        left: 20px;
    }

    .registry-case-wizard .print-label-zone span {
        font-size: font-size(small);
    }

    .registry-case-wizard .barcode-input {
        border: 1px solid #707070;
        border-radius: 0.2rem;
        padding-top: 1px;
    }

    .registry-case-wizard .input-group-prepend .input-group-text {
        border-radius: 14px;
        color: vt-color(search-bar-text);
        border: none;
        background-color: inherit;
        font-size: 12px;
        padding-left: 12px;
    }

    .registry-case-wizard .barcode-input input {
        width: 150px;
        border: none;
        color: vt-color(search-bar-text);
        background-color: inherit;
        font-size: 1.1rem;
    }

    .registry-case-wizard .wizard-home-height {
        min-height: 300px;
    }

    .registry-case-wizard .printed-label-switch-title {
        font-size: font-size(small);
    }

    .registry-case-wizard .wizard-icon {
        width: 50%;
        margin-bottom: 30px;
    }

    .registry-case-wizard .main-layout .vertical-line {
        width: 1px;
        height: 25px;
        background: vt-color(grid-general-border);
        margin: 0 10px;
    }

    .registry-case-wizard .main-layout .form p {
        color: vt-color(dark-text);
        margin-bottom: 5px;
    }

    .registry-case-wizard .number-input {
        width: 70px;
    }

    .registry-case-wizard .switch {
        position: relative;
        display: inline-block;
        width: 40px;
        height: 20px;
        background-color: rgba(0, 0, 0, 0.25);
        border-radius: 20px;
        transition: all 0.3s;
        cursor: pointer;
    }

    .registry-case-wizard .switch::after {
        content: "";
        position: absolute;
        width: 18px;
        height: 18px;
        border-radius: 18px;
        background-color: white;
        top: 1px;
        left: 1px;
        transition: all 0.3s;
    }

    .registry-case-wizard input[type="checkbox"]:checked + .switch::after {
        transform: translateX(20px);
    }

    .registry-case-wizard input[type="checkbox"]:checked + .switch {
        background-color: vt-color(primary);
    }

    .registry-case-wizard .offscreen {
        position: absolute;
        left: -9999px;
    }

    .registry-case-wizard .main-layout .title i {
        font-size: font-size-type(font-size-big-1);
    }

    .registry-case-wizard .main-layout .title h3 {
        font-size: 20px;
        margin-bottom: 0;
    }

    .registry-case-wizard .main-layout .title .icon-zone {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
    }

    .registry-case-wizard .btn__icon .btn__icon--primary.error {
        color: vt-color(red-medium) !important;
        transition: .3s;
    }

    .registry-case-wizard .btn__icon .btn__icon--primary.error:hover {
        color: vt-color(dark-red) !important;
    }

    .registry-case-wizard .main-layout .footer {
        position: absolute;
        bottom: 0;
        width: 100%;
    }

    .registry-case-wizard .main-layout .content {
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .registry-case-wizard .main-layout {
        flex: 1;
        position: relative;
    }

    .registry-case-wizard .tree-column .tree-block {
        margin-bottom: 15px;
    }

    .registry-case-wizard .tree-column .tree-item.active {
        background: vt-color(primary);
    }

    .registry-case-wizard .tree-column .tree-item.active i,
    .registry-case-wizard .tree-column .tree-item.active span {
        color: vt-color(white-light);
    }

    .registry-case-wizard .tree-column .tree-item.alert {
        background: vt-color(red-medium, .8);
    }

    .registry-case-wizard .tree-column .tree-item .alert-icon {
        color: vt-color(red-medium);
        font-size: 14px;
        margin-right: 2px;
    }

    .registry-case-wizard .tree-column .tree-item .trash-icon {
        display: none;
        color: vt-color(red-medium);
    }

    .registry-case-wizard .tree-column .tree-item.sample:hover .trash-icon,
    .registry-case-wizard .tree-column .tree-item.portion:hover .trash-icon,
    .registry-case-wizard .tree-column .tree-item.subportion:hover .trash-icon {
        display: inline-block;
    }

    .registry-case-wizard .tree-column .tree-item.sample:hover .alert-icon,
    .registry-case-wizard .tree-column .tree-item.sample:hover .new-icon,
    .registry-case-wizard .tree-column .tree-item.portion:hover .alert-icon,
    .registry-case-wizard .tree-column .tree-item.portion:hover .new-icon,
    .registry-case-wizard .tree-column .tree-item.subportion:hover .alert-icon,
    .registry-case-wizard .tree-column .tree-item.subportion:hover .new-icon {
        display: none;
    }

    .registry-case-wizard .tree-column .tree-item.new {
        background: vt-color(warning, .8);
    }

    .registry-case-wizard .tree-column .tree-item .new-icon {
        color: vt-color(warning);
        font-size: 10px;
    }

    .registry-case-wizard .tree-column .tree-item.portion {
        margin-left: 15px;
    }

    .registry-case-wizard .tree-column .tree-item.subportion {
        margin-left: 30px;
    }

    .registry-case-wizard .tree-column .tree-item {
        border-radius: 3px;
        border: 1px solid vt-color(grid-general-border);
        box-shadow: 0 1px 4px 0 rgba(141, 141, 143, 0.3);
        padding: 5px 10px;
        transition: .3s;
        cursor: pointer;
        margin-bottom: 4px;
    }

    .registry-case-wizard .tree-column .tree-item span {
        color: vt-color(dark-text);
    }

    .registry-case-wizard .tree-column .tree-item:hover {
        background: vt-color(light-primary);
    }

    .registry-case-wizard .tree-column {
        border-right: 1px solid vt-color(grid-general-border);
        width: 200px;
        height: 100%;
        box-shadow: 5px 0 5px -5px #b2b2b2;
    }

    .registry-case-wizard .tree-column p.column {
        font-size: font-size(very-tiny);
        text-transform: uppercase;
        margin: 10px 0;
    }

    .registry-case-wizard .wizard-button {
        background: vt-color(primary);
        color: vt-color(white-light);
        border-radius: 3px;
        cursor: pointer;
        transition: .3s;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .registry-case-wizard .wizard-button:hover {
        background: vt-color(alt-primary);
    }

    .registry-case-wizard .wizard-button + p {
        margin-top: 10px;
        font-size: font-size(normal);
        transition: .3s;
    }

    .registry-case-wizard .wizard-button.home {
        height: 70px;
        width: 80px;
        font-size: 28px;
    }

    .registry-case-wizard .wizard-button.column {
        height: 40px;
        width: 45px;
        font-size: 18px;
    }

    .registry-case-wizard p.home {
        font-size: font-size(big);
        margin-bottom: 10px;
    }

    .registry-case-wizard .modal-body {
        padding: 0;
        border-radius: .3rem;
        height: 510px !important;
        display: flex;
        flex-direction: column;
    }

    .registry-case-wizard .tree-column .tree-scroll-area {
        padding: 0 10px;
        overflow-y: auto;
        height: calc(53vh - 320px);
    }
</style>
